import React, { useRef, useState } from 'react';
import {
  Badge, Box, Checkbox, Flex, HStack, IconButton, Link, Menu, MenuButton, MenuItem, MenuList, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiArchive, FiCheck, FiDownload, FiPaperclip, FiRepeat, FiTrash2, FiUserPlus,
} from 'react-icons/fi';
import {
  DepartmentTask, DepartmentTeam, parseAssignees, TaskRecurrence, RECURRENCE_LABELS,
} from '../../hooks/useDepartmentTasks';
import { UserDto } from '../../models/user';
import UserChip from '../UserChip';
import {
  StoredFileScope, useStoredFiles, useStoredFileActions, downloadStoredFile,
} from '../../hooks/useStoredFiles';
import StoredFilePreviewModal, { PreviewFile } from '../StoredFilePreviewModal'; // item 935

// item 1466 — ONE task row for both surfaces (Tools › Department Tasks and a project's TASKS
// section): checkbox · text (+ optional "added by · date" meta and a project chip) · recurrence
// badge + picker · assignee avatars + picker · attachments · ARCHIVE (never delete — Chad).
// Lifted out of DepartmentTasksBoard so the two views can never drift.

export const DEPARTMENT_LABELS: Record<DepartmentTeam, string> = {
  [DepartmentTeam.Sales]: 'Sales',
  [DepartmentTeam.Development]: 'Development',
  [DepartmentTeam.Deployment]: 'Deployment',
  [DepartmentTeam.Accounting]: 'Accounting', // item 376
  [DepartmentTeam.Admin]: 'Admin', // item 477
};
export const DEPARTMENT_ORDER: DepartmentTeam[] = [
  DepartmentTeam.Sales, DepartmentTeam.Development, DepartmentTeam.Deployment, DepartmentTeam.Accounting, DepartmentTeam.Admin,
];

// item 1463 — a project-tied task wears its project: "Customer · Project", deep-linking to the
// project's TASKS section in a new tab (item 1460's #psec- expand-and-scroll).
export const ProjectChip: React.FC<{ task: DepartmentTask }> = ({ task }) => {
  if (!task.projectId) return null;
  const label = [task.customerName, task.projectName].filter(Boolean).join(' · ') || 'Project';
  return (
    <Link href={`/project/${task.projectId}#psec-tasks`} target="_blank" rel="noopener noreferrer" title="Open this project\'s tasks" _hover={{ textDecoration: 'none' }}>
      <Badge variant="outline" colorScheme="teal" fontSize="0.6rem" textTransform="none" whiteSpace="nowrap" cursor="pointer">
        {label}
      </Badge>
    </Link>
  );
};

// item 274 — per-task file attachments ride the existing StoredFile mechanism
// (scope=DepartmentTask, RefId=task id), mirroring the project-document pattern.
export const TaskAttachments: React.FC<{ taskId: string }> = ({ taskId }) => {
  const { data: files } = useStoredFiles(StoredFileScope.DepartmentTask, taskId);
  const { upload, remove, isUploading } = useStoredFileActions(StoredFileScope.DepartmentTask, taskId);
  const fileRef = useRef<HTMLInputElement>(null);
  const subColor = useColorModeValue('gray.600', 'whiteAlpha.700');
  // item 935 (Jake) — click opens the in-app preview modal (download lives inside it)
  const [preview, setPreview] = useState<PreviewFile | null>(null);

  return (
    <HStack spacing={2} mt={1} wrap="wrap">
      <StoredFilePreviewModal file={preview} onClose={() => setPreview(null)} />
      {(files ?? []).map((f) => (
        <Badge key={f.id} variant="subtle" colorScheme="green" cursor="pointer" onClick={() => setPreview({ id: f.id, name: f.name, contentType: f.contentType })}>
          <HStack spacing={1}>
            <FiDownload size={11} />
            <Text fontSize="xs">{f.name}</Text>
            <Box as="span" onClick={(e) => { e.stopPropagation(); remove(f.id); }} aria-label="Remove attachment">
              <FiTrash2 size={11} />
            </Box>
          </HStack>
        </Badge>
      ))}
      <input
        ref={fileRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
      />
      <IconButton
        aria-label="Attach a file"
        icon={<FiPaperclip />}
        size="xs"
        variant="ghost"
        isLoading={isUploading}
        onClick={() => fileRef.current?.click()}
        title="Attach a file"
      />
      {(files?.length ?? 0) === 0 && <Text fontSize="xs" color={subColor}>No files</Text>}
    </HStack>
  );
};

// item 339 — who's on this task: an avatar stack (profile photos / initials) + a
// people-picker to add or remove teammates. Toggling a user posts the new set.
export const TaskAssignees: React.FC<{
  task: DepartmentTask;
  users: UserDto[];
  onSet: (userIds: string[]) => void;
}> = ({ task, users, onSet }) => {
  const assigned = parseAssignees(task.assigneeUserIds);
  const byId = new Map(users.map((u) => [u.id, u]));
  const toggle = (id: string) => onSet(
    assigned.includes(id) ? assigned.filter((a) => a !== id) : [...assigned, id],
  );
  return (
    <HStack spacing={1} mr={1}>
      {assigned.map((id) => {
        const u = byId.get(id);
        if (!u) return null;
        return (
          <Box key={id} title={u.name}>
            <UserChip userId={u.id} name={u.name} photoUrl={u.photoUrl} size="sm" showName={false} />
          </Box>
        );
      })}
      <Menu closeOnSelect={false}>
        <MenuButton
          as={IconButton}
          aria-label="Assign teammates"
          title="Assign teammates to this task"
          icon={<FiUserPlus />}
          size="xs"
          variant="ghost"
        />
        <MenuList fontSize="sm" maxH="280px" overflowY="auto">
          {users.map((u) => (
            <MenuItem
              key={u.id}
              icon={assigned.includes(u.id) ? <FiCheck /> : <Box w="14px" />}
              onClick={() => toggle(u.id)}
            >
              <HStack spacing={2}>
                <UserChip userId={u.id} name={u.name} photoUrl={u.photoUrl} size="sm" showName={false} />
                <Text>{u.name}</Text>
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </HStack>
  );
};

interface TaskRowProps {
  task: DepartmentTask;
  users: UserDto[];
  onToggle: (id: string, done: boolean) => void;
  onArchive: (id: string) => void;
  onAssign: (id: string, userIds: string[]) => void;
  onRecurrence: (id: string, recurrence: TaskRecurrence) => void;
  flash?: boolean; // item 934 — deep-linked task rings for a few seconds
  mine?: boolean; // item 1466 — assigned to the signed-in user: red left edge
  showProject?: boolean; // the global board shows the chip; the project section doesn't need it
  showMeta?: boolean; // "added by · date" under the text (project section)
  showDepartment?: boolean; // MY TASKS band: which list it lives in
}

const fmtDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(/[zZ]$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const TaskRow: React.FC<TaskRowProps> = ({
  task: t, users, onToggle, onArchive, onAssign, onRecurrence, flash, mine, showProject, showMeta, showDepartment,
}) => {
  const border = useColorModeValue('gray.200', 'whiteAlpha.300');
  const doneColor = useColorModeValue('gray.400', 'whiteAlpha.500');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const mineBg = useColorModeValue('rgba(200,16,46,0.04)', 'rgba(200,16,46,0.08)');
  const addedBy = t.createdById ? users.find((u) => u.id === t.createdById)?.name : undefined;
  return (
    <Box
      id={`task-${t.id}`}
      borderWidth="1px"
      borderColor={flash ? 'purple.400' : border}
      borderLeftWidth={mine ? '3px' : undefined}
      borderLeftColor={mine ? 'red.500' : undefined}
      bg={mine ? mineBg : undefined}
      boxShadow={flash ? '0 0 0 2px var(--chakra-colors-purple-400)' : undefined}
      borderRadius="md"
      p={2}
      opacity={t.pending ? 0.6 : 1}
      transition="box-shadow 0.4s, border-color 0.4s, opacity 0.2s"
    >
      <Flex align="center">
        <Checkbox
          isChecked={t.isDone}
          isDisabled={!!t.pending}
          onChange={(e) => onToggle(t.id, e.target.checked)}
          mr={2}
        />
        <Box flex={1} minW={0}>
          <HStack spacing={2} align="center" flexWrap="wrap">
            <Text
              fontSize="sm"
              as={t.isDone ? 's' : undefined}
              color={t.isDone ? doneColor : undefined}
            >
              {t.text}
            </Text>
            {showDepartment && (
              <Badge variant="subtle" fontSize="0.6rem">{DEPARTMENT_LABELS[t.department] ?? '—'}</Badge>
            )}
            {showProject && <ProjectChip task={t} />}
          </HStack>
          {showMeta && (
            <Text fontSize="xs" color={subColor} mt={0.5}>
              {[addedBy ? `added by ${addedBy}` : null, fmtDate(t.createdDate)].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Box>
        {/* item 399 — recurrence: badge + picker (completion respawns) */}
        {(t.recurrence ?? TaskRecurrence.None) !== TaskRecurrence.None && (
          <Badge colorScheme="purple" variant="subtle" fontSize="0.6rem" mr={1} whiteSpace="nowrap">
            {`↻ ${RECURRENCE_LABELS[t.recurrence ?? TaskRecurrence.None]}${t.dueDate ? ` · due ${fmtDate(t.dueDate)}` : ''}`}
          </Badge>
        )}
        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Set recurrence"
            icon={<FiRepeat />}
            size="xs"
            variant="ghost"
            isDisabled={!!t.pending}
            opacity={(t.recurrence ?? 0) !== 0 ? 1 : 0.45}
          />
          <MenuList fontSize="sm">
            {[TaskRecurrence.None, TaskRecurrence.Weekly, TaskRecurrence.BiWeekly, TaskRecurrence.Monthly, TaskRecurrence.Annually].map((r) => (
              <MenuItem
                key={r}
                icon={(t.recurrence ?? TaskRecurrence.None) === r ? <FiCheck /> : <Box w="14px" />}
                onClick={() => onRecurrence(t.id, r)}
              >
                {RECURRENCE_LABELS[r]}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        {/* item 339 — assignee avatars + people-picker */}
        <TaskAssignees task={t} users={users} onSet={(ids) => onAssign(t.id, ids)} />
        {/* item 1465 (Chad: "once a task is added, it should not be able to be deleted") — ARCHIVE,
            not delete: the row moves to the Archived section and can be restored. */}
        <Tooltip label="Archive task — it moves to Archived and can be restored" openDelay={400}>
          <IconButton
            aria-label="Archive task"
            icon={<FiArchive />}
            size="xs"
            variant="ghost"
            isDisabled={!!t.pending}
            onClick={() => onArchive(t.id)}
          />
        </Tooltip>
      </Flex>
      {!t.pending && (
        <Box pl={6}>
          <TaskAttachments taskId={t.id} />
        </Box>
      )}
    </Box>
  );
};

TaskRow.defaultProps = {
  flash: false, mine: false, showProject: false, showMeta: false, showDepartment: false,
};

export default TaskRow;
